import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"],
  _excluded2 = ["visualization", "debug", "gates", "onReportVisualize"];
import { Record } from 'immutable';
import { memo, useEffect, useMemo } from 'react';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { DEFAULT_DEBUG } from './constants/debug';
import * as Datawell from './visualizations/datawell/datawellVisualization';
// @ts-expect-error Imported module has no declaration file
import * as Pivot from './visualizations/pivot/pivotVisualization';
// @ts-expect-error Imported module has no declaration file
import * as Sliced from './visualizations/sliced/slicedVisualization';
import * as Table from './visualizations/table/tableVisualization';
// @ts-expect-error Imported module has no declaration file
import * as XY from './visualizations/xy/xyVisualization';
// @ts-expect-error Imported module has no declaration file
import * as Waterfall from './visualizations/waterfall/waterfallVisualization';
// @ts-expect-error Imported module has no declaration file
import * as Funnel from './visualizations/funnel/funnelVisualization';
// @ts-expect-error Imported module has no declaration file
import * as CrossTab from './visualizations/crossTab/crossTabVisualization';
import * as Sankey from './visualizations/sankey/sankeyVisualization';
import * as Gauge from './visualizations/gauge/GaugeVisualization';
import { DEBUG_EVENTS } from './constants/debug';
import { filterTokenProps } from 'foundations-components/utils/filterTokenProps';
import styled from 'styled-components';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * All visualizations need to expose 3 things:
 *   type: string,
 *   record: checked.record constructor
 *   Render: React.Component
 */

export const VISUALIZATIONS = {
  [Datawell.type]: Datawell,
  [Pivot.type]: Pivot,
  [Sliced.type]: Sliced,
  [Table.type]: Table,
  [XY.type]: XY,
  [Waterfall.type]: Waterfall,
  [Funnel.type]: Funnel,
  [CrossTab.type]: CrossTab,
  [Sankey.type]: Sankey,
  [Gauge.type]: Gauge
};
const constructVisualization = (record, visualization) => visualization instanceof Record ? visualization : record(visualization);
const VizContainer = styled.div.withConfig({
  displayName: "Visualization__VizContainer",
  componentId: "peh4sb-0"
})(({
  $cssStyles
}) => $cssStyles);
const VisualizationContainer = _ref => {
  let {
      children
    } = _ref,
    styleProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const defaultStyle = {
    display: 'flex',
    flexGrow: 1
  };
  const {
    tokenProps
  } = filterTokenProps(styleProps);
  const customContainerStyle = Object.assign({}, defaultStyle, tokenProps);
  const cssStyles = useMapTokenPropsToCSS(customContainerStyle);
  return /*#__PURE__*/_jsx(VizContainer, {
    $cssStyles: Object.assign({}, cssStyles),
    children: children
  });
};
const Visualization = _ref2 => {
  let {
      visualization,
      debug = DEFAULT_DEBUG,
      gates = [],
      onReportVisualize
    } = _ref2,
    styleProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const type = visualization.get ? visualization.get('type') : visualization.type;
  const viz = VISUALIZATIONS[type];
  const sanitizedVisualization = useMemo(() => {
    return viz && constructVisualization(viz.record, visualization);
  }, [visualization, viz]);
  useEffect(() => {
    if (sanitizedVisualization) {
      debug.set(DEBUG_EVENTS.VISUALIZATION, sanitizedVisualization);
    } else {
      debug.set(DEBUG_EVENTS.ERROR, `Unknown visualization for type ${type}`);
    }
  }, [debug, type, sanitizedVisualization]);
  const maybeVisualizationSpecificContainerStyle = viz && viz.VisualizationContainerStyle || {};
  if (!viz) {
    return /*#__PURE__*/_jsx(VisualizationContainer, Object.assign({
      gates: gates
    }, styleProps, {
      children: /*#__PURE__*/_jsx(Illustration, {
        name: "errors/general",
        width: "50%",
        margin: "auto"
      })
    }));
  }
  const {
    Render
  } = viz;
  return /*#__PURE__*/_jsx(VisualizationContainer, Object.assign({
    gates: gates
  }, maybeVisualizationSpecificContainerStyle, styleProps, {
    children: /*#__PURE__*/_jsx(Render, {
      visualization: sanitizedVisualization,
      onReportVisualize: onReportVisualize,
      debug: debug,
      gates: gates
    })
  }));
};
export default /*#__PURE__*/memo(Visualization);